// lib/quality-absolute-lens.ts
//
// Universe-absolute quality lens (Steve directive 2026-06-22). Quality pillars
// are normally z-scored WITHIN peer group, so a top-tier business in a tough
// group (MSFT in `technology`) is graded on the hardest curve and understated.
// This lens recomputes Profitability + Growth against the ENTIRE universe, rolls
// them up with the peer-z Safety + Shareholder-Returns pillars, and blends
//   qualityScoreBlended = 0.75 * qualityScore(peer-z) + 0.25 * qualityScoreAbsolute
// at the AXIS level (never re-z'd after) — the exact mirror of valuationScoreBlended.
// Only Profitability + Growth get the absolute lens: their factor LEVELS travel
// across sectors. Safety (leverage/coverage) and SR (payout policy) are sector-
// shaped, so they stay pure peer-z.

import type { StockFactors, ScoredStock } from './types';
import { computeStats, zscoreVal, clamp, winsorize } from './scoring-helpers';
import { PILLAR_FACTORS } from './pillar-factors';
import { DEFAULT_PILLAR_WEIGHTS_DECIMAL, QUALITY_PILLAR_KEYS } from './factor-config';

/** 0.75 peer-z + 0.25 universe-absolute. */
export const QUALITY_BLEND_WEIGHT = 0.75;

type FactorSpec = { key: keyof StockFactors; invert: boolean; weight?: number };

/** Standardized universe-z pillar value per stock for one factor list.
 *  Winsorize P1/P99 + z-score each factor against the WHOLE population, weighted-
 *  average, then standardize the pillar across the universe (mirrors how the peer-z
 *  pillarScores are standardized within group). */
function universePillarZ(
  stocks: { symbol: string; factors: StockFactors }[],
  factors: FactorSpec[],
): Map<string, number | null> {
  const wins: Partial<Record<keyof StockFactors, (number | null)[]>> = {};
  const stats: Partial<Record<keyof StockFactors, { mean: number; std: number } | null>> = {};
  for (const { key } of factors) {
    wins[key] = winsorize(stocks.map(s => s.factors[key] as number | null));
    stats[key] = computeStats(wins[key]!);
  }
  const raw = new Map<string, number | null>();
  stocks.forEach((s, idx) => {
    let wSum = 0, vSum = 0;
    for (const { key, invert, weight } of factors) {
      const wVal = wins[key] ? wins[key]![idx] : (s.factors[key] as number | null);
      const zv = clamp(zscoreVal(wVal, stats[key] ?? null));
      if (zv !== null) { const w = weight ?? 1; vSum += (invert ? -zv : zv) * w; wSum += w; }
    }
    raw.set(s.symbol, wSum > 0 ? vSum / wSum : null);
  });
  const pillarStats = computeStats([...raw.values()]);
  const out = new Map<string, number | null>();
  for (const [sym, r] of raw) out.set(sym, clamp(zscoreVal(r, pillarStats)));
  return out;
}

export function applyQualityAbsoluteLens(scored: ScoredStock[]): ScoredStock[] {
  const profU = universePillarZ(scored, PILLAR_FACTORS.nonFinancial.profitability as FactorSpec[]);
  const growU = universePillarZ(scored, PILLAR_FACTORS.nonFinancial.growth as FactorSpec[]);
  const W = DEFAULT_PILLAR_WEIGHTS_DECIMAL;

  // Rollup: prof/growth universe-z + safety/SR peer-z, weighted by pillar weights.
  const rollup = new Map<string, number | null>();
  for (const s of scored) {
    const vals: Record<string, number | null> = {
      profitability: profU.get(s.symbol) ?? null,
      growth: growU.get(s.symbol) ?? null,
      safety: s.pillarScores.safety ?? null,
      shareholderReturns: s.pillarScores.shareholderReturns ?? null,
    };
    let wSum = 0, vSum = 0;
    for (const k of QUALITY_PILLAR_KEYS) {
      const v = vals[k];
      if (v != null && Number.isFinite(v)) { vSum += v * W[k]; wSum += W[k]; }
    }
    rollup.set(s.symbol, wSum > 0 ? vSum / wSum : null);
  }
  const rollupStats = computeStats([...rollup.values()]);

  return scored.map(s => {
    const qAbs = clamp(zscoreVal(rollup.get(s.symbol) ?? null, rollupStats));
    const qPeer = s.qualityScore;
    const qBlend = (qAbs == null || qPeer == null)
      ? qPeer ?? null
      : QUALITY_BLEND_WEIGHT * qPeer + (1 - QUALITY_BLEND_WEIGHT) * qAbs;
    return { ...s, qualityScoreAbsolute: qAbs, qualityScoreBlended: qBlend };
  });
}
